/**
 * logo-card.js — touch support for the `.logo-card` hover popovers.
 *
 * On hover-capable devices the popover shows on hover/focus (pure CSS). On
 * coarse-pointer (touch) devices there's no hover, so:
 *   • tap a logo  → open its card
 *   • tap outside → close
 *   • tap the same logo again → close
 *
 * While open the popover is portaled to <body> and positioned `fixed`, clamped
 * to the viewport. That deliberately escapes the `.eval-grid { overflow: clip }`
 * and the `backdrop-filter` containing block on frame-and-assure's cards — both
 * of which would otherwise crop an in-place popover on small screens (which is
 * why it was previously suppressed on touch).
 */
const isTouch = window.matchMedia('(hover: none)').matches;

if (isTouch) {
  let openPop = null; // the popover currently shown
  let openCard = null; // its .logo-card
  let homeParent = null; // where the popover lived, to restore on close

  function closeCard() {
    if (!openPop) return;
    openPop.classList.remove('is-open');
    openPop.style.position = openPop.style.left = openPop.style.top = '';
    if (homeParent) homeParent.appendChild(openPop); // put it back inside its logo-card
    if (openCard) openCard.setAttribute('aria-expanded', 'false');
    openPop = openCard = homeParent = null;
  }

  function openForCard(card) {
    const pop = card.querySelector('.logo-card__pop');
    if (!pop) return;
    closeCard();
    homeParent = pop.parentNode;
    openCard = card;
    openPop = pop;

    // Portal to <body> so no clipping/containing-block ancestor affects it.
    document.body.appendChild(pop);
    pop.classList.add('is-open');
    pop.style.position = 'fixed';
    pop.style.left = '0px';
    pop.style.top = '0px'; // reset before measuring

    // Centre above the logo, then clamp within the viewport (8px margin).
    const cr = card.getBoundingClientRect();
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    let left = cr.left + cr.width / 2 - pw / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
    let top = cr.top - ph - 10; // above the logo
    if (top < 8) top = cr.bottom + 10; // flip below if there's no room above
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
    card.setAttribute('aria-expanded', 'true');
  }

  document.querySelectorAll('.logo-card').forEach((card) => {
    // It's a tap-to-expand disclosure: role="button" makes aria-expanded valid
    // (a role-less <span> doesn't support it) and announces it correctly.
    card.setAttribute('role', 'button');
    card.setAttribute('aria-expanded', 'false');
    const toggle = (e) => {
      e.stopPropagation(); // don't let this bubble to the outside-close handler
      if (openCard === card) closeCard();
      else openForCard(card);
    };
    card.addEventListener('click', toggle);
    // Keyboard: Enter/Space activate it like a real button.
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        toggle(e);
      }
    });
  });

  // Keep taps inside an open popover from closing it (links still navigate).
  document.querySelectorAll('.logo-card__pop').forEach((pop) => {
    pop.addEventListener('click', (e) => e.stopPropagation());
  });

  // Tap anywhere else → close. Scroll/resize invalidates the fixed coords → close.
  document.addEventListener('click', closeCard);
  window.addEventListener('scroll', closeCard, true);
  window.addEventListener('resize', closeCard);
}
